<template>
  <div class="platform">
    <header class="platform__header">
      <div class="platform__brand">
        <span class="platform__brand-mark">S</span>
        <div>
          <p>Sauda</p>
          <h1>{{ $t('platform.title') }}</h1>
        </div>
      </div>

      <div class="platform__actions">
        <LanguageSwitcher />
        <button type="button" class="platform__ghost" @click="$router.push('/admin_panel')">
          <i class="mdi mdi-view-dashboard-outline"></i>
          {{ $t('platform.myStore') }}
        </button>
        <button type="button" class="platform__logout" @click="logout">
          <i class="mdi mdi-logout"></i>
          {{ $t('platform.logout') }}
        </button>
      </div>
    </header>

    <main class="platform__main">
      <section class="platform__intro">
        <div>
          <span class="platform__eyebrow"><i class="mdi mdi-shield-crown-outline"></i> {{ $t('platform.ownerOnly') }}</span>
          <h2>{{ $t('platform.connectedBusinesses') }}</h2>
          <p>{{ $t('platform.connectedText') }}</p>
        </div>
        <label class="platform__search">
          <i class="mdi mdi-magnify"></i>
          <input
            v-model.trim="search"
            type="search"
            :placeholder="$t('platform.search')"
            @input="onSearch"
          >
        </label>
      </section>

      <section class="platform__stats">
        <article class="platform-stat">
          <span>{{ $t('platform.companiesTotal') }}</span>
          <strong>{{ summary.companies_total || 0 }}</strong>
          <i class="mdi mdi-storefront-outline"></i>
        </article>
        <article class="platform-stat platform-stat--success">
          <span>{{ $t('platform.selling') }}</span>
          <strong>{{ summary.companies_with_sales || 0 }}</strong>
          <i class="mdi mdi-chart-line"></i>
        </article>
        <article class="platform-stat">
          <span>{{ $t('platform.productsAdded') }}</span>
          <strong>{{ summary.products_total || 0 }}</strong>
          <i class="mdi mdi-package-variant-closed"></i>
        </article>
        <article class="platform-stat platform-stat--accent">
          <span>{{ $t('platform.totalRevenue') }}</span>
          <strong>{{ formatMoney(summary.revenue_total) }}</strong>
          <i class="mdi mdi-cash-multiple"></i>
        </article>
      </section>

      <section class="platform__section">
        <header class="platform__section-head">
          <div>
            <h2>{{ $t('platform.companies') }}</h2>
            <p>{{ total }} {{ $t('platform.connections') }}</p>
          </div>
          <button type="button" class="platform__refresh" :disabled="loading" @click="load">
            <i class="mdi" :class="loading ? 'mdi-loading mdi-spin' : 'mdi-refresh'"></i>
            {{ $t('common.refresh') }}
          </button>
        </header>

        <div v-if="loading && !companies.length" class="platform__loading">
          <i class="mdi mdi-loading mdi-spin"></i>
          {{ $t('common.loading') }}
        </div>

        <div v-else-if="companies.length" class="company-list">
          <article v-for="company in companies" :key="company.id" class="company-card">
            <div class="company-card__top">
              <div class="company-card__identity">
                <div class="company-card__icon"><i class="mdi mdi-storefront-outline"></i></div>
                <div>
                  <div class="company-card__title-row">
                    <h3>{{ company.name }}</h3>
                    <span v-if="company.is_blocked" class="company-card__status company-card__status--blocked">{{ $t('platform.blocked') }}</span>
                    <span v-else class="company-card__status">{{ $t('platform.active') }}</span>
                  </div>
                  <p>{{ company.owner_name || $t('platform.ownerMissing') }}<span v-if="company.owner_email"> · {{ company.owner_email }}</span></p>
                </div>
              </div>
              <div class="company-card__meta">
                <span>{{ company.sale_mode === 'quick' ? $t('platform.quick') : $t('platform.catalog') }}</span>
                <time>{{ $t('platform.connected') }} {{ formatDate(company.created_at) }}</time>
              </div>
            </div>

            <div class="company-card__metrics">
              <div><span>{{ $t('platform.categories') }}</span><strong>{{ company.categories_count }}</strong></div>
              <div><span>{{ $t('platform.products') }}</span><strong>{{ company.products_count }}</strong></div>
              <div><span>{{ $t('platform.cashiers') }}</span><strong>{{ company.cashiers_count }}</strong></div>
              <div><span>{{ $t('platform.sales') }}</span><strong>{{ company.orders_count }}</strong></div>
              <div class="company-card__revenue"><span>{{ $t('platform.revenue') }}</span><strong>{{ formatMoney(company.revenue_total) }}</strong></div>
            </div>

            <footer class="company-card__footer">
              <span><i class="mdi mdi-clock-outline"></i> {{ company.last_sale_at ? `${$t('platform.lastSale')} ${formatDate(company.last_sale_at, true)}` : $t('platform.noSales') }}</span>
            </footer>
          </article>
        </div>

        <div v-else class="platform__empty">
          <i class="mdi mdi-store-search-outline"></i>
          <strong>{{ search ? $t('platform.companiesNotFound') : $t('platform.noCompanies') }}</strong>
          <span>{{ search ? $t('platform.changeQuery') : $t('platform.noConnectionsText') }}</span>
        </div>

        <div v-if="pageCount > 1" class="platform__pagination">
          <v-pagination v-model="page" :length="pageCount" :total-visible="6" color="#0b4f8a" />
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { getPlatformDashboard } from '@/api/platformAdmin'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { notifyError } from '@/utils/notify'

export default {
  name: 'PlatformAdmin',
  components: {
    LanguageSwitcher,
  },
  data() {
    return {
      loading: false,
      companies: [],
      summary: {},
      total: 0,
      page: 1,
      pageCount: 0,
      search: '',
      searchTimer: null,
    }
  },
  watch: {
    page() {
      this.load()
    },
  },
  mounted() {
    if (!localStorage.getItem('access_token')) {
      this.$router.replace('/super-admin-login')
      return
    }
    this.load()
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const data = await getPlatformDashboard({
          page: this.page,
          per_page: 15,
          search: this.search || undefined,
        })
        this.companies = Array.isArray(data.data) ? data.data : []
        this.summary = data.summary || {}
        this.total = Number(data.total) || 0
        this.pageCount = Number(data.last_page) || 0
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.$router.replace('/super-admin-login')
          return
        }
        notifyError(error)
      } finally {
        this.loading = false
      }
    },
    onSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        if (this.page !== 1) this.page = 1
        else this.load()
      }, 300)
    },
    formatMoney(value) {
      return `${new Intl.NumberFormat(this.$i18n.locale === 'kk' ? 'kk-KZ' : 'ru-RU').format(Number(value) || 0)} ₸`
    },
    formatDate(value, withTime = false) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '—'
      return new Intl.DateTimeFormat(this.$i18n.locale === 'kk' ? 'kk-KZ' : 'ru-RU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
      }).format(date)
    },
    logout() {
      const storageKeys = ['access_token', 'role', 'company_role', 'can_manage_products', 'user_name', 'sale_mode', 'is_platform_admin']
      storageKeys
        .forEach((key) => localStorage.removeItem(key))
      this.$router.push('/kassa-login')
    },
  },
}
</script>

<style scoped lang="scss">
.platform {
  min-height: 100vh;
  background: #f3f6fa;
  color: #152033;
}

.platform__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 14px max(20px, calc((100% - 1220px) / 2));
  background: #10243a;
  color: #fff;
}

.platform__brand,
.platform__actions,
.company-card__identity,
.company-card__title-row,
.company-card__footer,
.platform__refresh {
  display: flex;
  align-items: center;
}

.platform__brand { gap: 10px; }
.platform__brand-mark {
  width: 38px; height: 38px; display: grid; place-items: center; border-radius: 11px;
  background: #fff; color: #0b4f8a; font-weight: 850; font-size: 18px;
}
.platform__brand p { margin: 0; font-size: 12px; opacity: .7; }
.platform__brand h1 { margin: 1px 0 0; font-size: 16px; font-weight: 750; }
.platform__actions { gap: 8px; }
.platform__actions button {
  display: inline-flex; align-items: center; gap: 7px; padding: 9px 12px; border-radius: 10px;
  color: #fff; font-size: 13px; font-weight: 650; cursor: pointer;
}
.platform__ghost { border: 1px solid rgba(255,255,255,.2); background: rgba(255,255,255,.08); }
.platform__logout { border: 0; background: transparent; }

.platform__main { max-width: 1220px; margin: 0 auto; padding: 32px 20px 48px; }
.platform__intro { display: flex; justify-content: space-between; align-items: end; gap: 20px; margin-bottom: 24px; }
.platform__eyebrow { display: inline-flex; gap: 6px; color: #0b4f8a; font-size: 12px; font-weight: 750; text-transform: uppercase; letter-spacing: .05em; }
.platform__intro h2 { margin: 8px 0 6px; font-size: 29px; letter-spacing: -.03em; }
.platform__intro p { margin: 0; color: #6b7a90; }
.platform__search { position: relative; width: min(350px, 100%); }
.platform__search .mdi { position: absolute; left: 13px; top: 12px; color: #8ea0b2; font-size: 19px; }
.platform__search input { width: 100%; padding: 12px 14px 12px 40px; border: 1px solid #d9e2ec; border-radius: 12px; background: #fff; outline: none; font-weight: 600; }
.platform__search input:focus { border-color: #0b4f8a; box-shadow: 0 0 0 3px rgba(11,79,138,.1); }

.platform__stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
.platform-stat { position: relative; overflow: hidden; padding: 17px; background: #fff; border: 1px solid #d9e2ec; border-radius: 16px; }
.platform-stat span { display: block; color: #6b7a90; font-size: 12px; font-weight: 700; }
.platform-stat strong { display: block; margin-top: 8px; font-size: 23px; letter-spacing: -.03em; }
.platform-stat > .mdi { position: absolute; right: 14px; bottom: 12px; color: #dbe8f3; font-size: 34px; }
.platform-stat--success strong { color: #087443; }
.platform-stat--accent strong { color: #0b4f8a; font-size: 20px; }

.platform__section { padding: 20px; background: #fff; border: 1px solid #d9e2ec; border-radius: 20px; }
.platform__section-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.platform__section-head h2 { margin: 0; font-size: 18px; }
.platform__section-head p { margin: 4px 0 0; color: #6b7a90; font-size: 13px; }
.platform__refresh { gap: 7px; padding: 9px 11px; border: 1px solid #d9e2ec; border-radius: 10px; background: #fff; color: #3d4f63; cursor: pointer; font-weight: 650; }
.platform__refresh:hover:not(:disabled) { color: #0b4f8a; border-color: #0b4f8a; }

.company-list { display: flex; flex-direction: column; gap: 10px; }
.company-card { border: 1px solid #e0e8f0; border-radius: 15px; overflow: hidden; }
.company-card__top { display: flex; justify-content: space-between; gap: 16px; padding: 15px 16px; }
.company-card__identity { gap: 12px; min-width: 0; }
.company-card__icon { width: 42px; height: 42px; flex-shrink: 0; display: grid; place-items: center; border-radius: 12px; background: #eef4fa; color: #0b4f8a; }
.company-card__icon .mdi { font-size: 21px; }
.company-card__title-row { gap: 8px; flex-wrap: wrap; }
.company-card h3 { margin: 0; font-size: 15px; }
.company-card__identity p { margin: 4px 0 0; color: #6b7a90; font-size: 12.5px; overflow-wrap: anywhere; }
.company-card__status { padding: 3px 7px; border-radius: 99px; background: #eaf7f0; color: #087443; font-size: 10px; font-weight: 750; }
.company-card__status--blocked { background: #fff0ef; color: #b42318; }
.company-card__meta { text-align: right; color: #6b7a90; font-size: 12px; }
.company-card__meta span { display: inline-block; padding: 4px 8px; border-radius: 99px; background: #eef4fa; color: #0b4f8a; font-weight: 700; }
.company-card__meta time { display: block; margin-top: 6px; }
.company-card__metrics { display: grid; grid-template-columns: repeat(5, 1fr); border-top: 1px solid #edf1f5; border-bottom: 1px solid #edf1f5; }
.company-card__metrics div { padding: 11px 16px; border-right: 1px solid #edf1f5; }
.company-card__metrics div:last-child { border-right: 0; }
.company-card__metrics span { display: block; color: #6b7a90; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; }
.company-card__metrics strong { display: block; margin-top: 4px; font-size: 16px; }
.company-card__revenue strong { color: #0b4f8a; }
.company-card__footer { gap: 6px; padding: 9px 16px; color: #6b7a90; font-size: 12px; }

.platform__loading, .platform__empty { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 230px; gap: 8px; color: #6b7a90; text-align: center; }
.platform__loading { flex-direction: row; }
.platform__empty .mdi { color: #9eb6cc; font-size: 40px; }
.platform__empty strong { color: #152033; }
.platform__pagination { display: flex; justify-content: center; margin-top: 22px; }

@media (max-width: 900px) {
  .platform__stats { grid-template-columns: repeat(2, 1fr); }
  .company-card__metrics { grid-template-columns: repeat(3, 1fr); }
  .company-card__metrics div:nth-child(3) { border-right: 0; }
  .company-card__metrics div:nth-child(-n+3) { border-bottom: 1px solid #edf1f5; }
}
@media (max-width: 640px) {
  .platform__header, .platform__intro, .company-card__top { align-items: flex-start; flex-direction: column; }
  .platform__header { padding: 12px 14px; }
  .platform__actions { width: 100%; }
  .platform__actions button { flex: 1; justify-content: center; }
  .platform__main { padding: 22px 14px 36px; }
  .platform__intro h2 { font-size: 25px; }
  .platform__search { width: 100%; }
  .platform__stats { grid-template-columns: 1fr 1fr; }
  .platform__section { padding: 14px; }
  .company-card__meta { text-align: left; padding-left: 54px; }
  .company-card__metrics { grid-template-columns: 1fr 1fr; }
  .company-card__metrics div { border-bottom: 1px solid #edf1f5; }
  .company-card__metrics div:nth-child(2n) { border-right: 0; }
  .company-card__metrics div:last-child { grid-column: span 2; border-bottom: 0; }
}
</style>
